import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Right-hand slide-over used by "What data do you need?" (§3) and
 * "Learn more" on the floor banner (§6).
 */
export function SidePanel({
    open,
    title,
    onClose,
    children,
    /** Raise this when the panel opens from inside another overlay. */
    zClassName = "z-[1000]"
}: {
    open: boolean;
    title: string;
    onClose: () => void;
    children: React.ReactNode;
    zClassName?: string;
}) {
    useEffect(() => {
        if (!open || typeof document === "undefined") return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open || typeof document === "undefined") return null;

    return createPortal(
        <div className={`fixed inset-0 ${zClassName}`}>
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={onClose} />
            <aside
                role="dialog"
                aria-label={title}
                className="absolute inset-y-0 right-0 flex w-full max-w-[440px] flex-col bg-white shadow-2xl">
                <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
                    <h2 className="text-[15px] font-semibold text-gray-900">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="-mr-1 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
                        <X className="h-4 w-4" strokeWidth={2} />
                    </button>
                </header>
                <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
            </aside>
        </div>,
        document.body
    );
}
